// A field worker's own view on the web.
//
// The real home for this is the phone (phase 5) — this is the same data, so
// somebody without the app installed is not locked out. Deliberately a
// chronological list rather than a calendar: the question is "where am I going
// and when", not "what does the month look like".
import { useEffect, useState } from 'react';
import { scheduling, type MyAssignment } from '../schedulingApi';
import { ApiError } from '../api';
import { addDays, dateRangeLabel, todayISO } from '../lib/dates';
import { Banner } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { Documents } from '../components/Documents';
import { useOptionalSession } from '../session';
import { MyWeeks } from '../components/timesheets/MyWeeks';

export function MySchedulePage() {
  const [assignments, setAssignments] = useState<MyAssignment[]>([]);
  const [resourceName, setResourceName] = useState<string | null>(null);
  const [linked, setLinked] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Round 44: this week's timesheet, and last week's if it is owed, first
  // thing on a worker's landing page, for a company that uses them.
  const timesheetsOn = useOptionalSession()?.tenant?.timesheetsEnabled ?? false;

  useEffect(() => {
    const today = todayISO();
    void scheduling
      .mySchedule({ from: today, to: addDays(today, 28) })
      .then((result) => {
        setLinked(result.resource !== null);
        setResourceName(result.resource?.name ?? null);
        setAssignments(result.assignments);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load your schedule.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <Banner kind="error">{error}</Banner>;

  if (!linked) {
    return (
      <>
        {/* A timesheet keys on the login, not the resource, so it is here
            even before anybody links the account. */}
        {timesheetsOn && <MyWeeks compact />}
        <section className="panel">
          <h1>My schedule</h1>
          <p className="muted">
            Your account is not linked to a resource yet, so there is nothing to show. Ask whoever
            runs your Dayboard to link you; it takes them a moment.
          </p>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader title="My schedule" actions={<span className="muted">{resourceName}</span>} />
      {timesheetsOn && <MyWeeks compact />}

      {assignments.length === 0 ? (
        <section className="panel">
          <p className="muted">Nothing booked in the next four weeks.</p>
        </section>
      ) : (
        assignments.map((assignment) => (
          <section className="panel" key={assignment.bookingId}>
            <div className="row mb-3">
              <h2 className="!mb-0">
                {assignment.job.number} · {assignment.job.title}
              </h2>
              <span className="spacer" />
              <span className="pill">{assignment.job.status.replace('_', ' ')}</span>
            </div>
            <dl className="details">
              <dt>When</dt>
              <dd>
                {dateRangeLabel(assignment.startDate, assignment.endDate)}
                {assignment.startTime
                  ? `, ${assignment.startTime}–${assignment.endTime}`
                  : ', all day'}
              </dd>
              {assignment.job.clientName && (
                <>
                  <dt>Client</dt>
                  <dd>{assignment.job.clientName}</dd>
                </>
              )}
              {assignment.job.siteAddress && (
                <>
                  <dt>Site</dt>
                  <dd>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        assignment.job.siteAddress
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {assignment.job.siteAddress}
                    </a>
                  </dd>
                </>
              )}
              {assignment.job.description && (
                <>
                  <dt>Details</dt>
                  <dd>{assignment.job.description}</dd>
                </>
              )}
              {assignment.note && (
                <>
                  <dt>Note</dt>
                  <dd>{assignment.note}</dd>
                </>
              )}
            </dl>

            {/* The whole point of the product: the drawings and safety
                documents for this job, on the phone, before you leave. */}
            <Documents
              kind="job"
              ownerId={assignment.job.id}
              title="Documents for this job"
            />
          </section>
        ))
      )}
    </>
  );
}
